import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { DollarSign, Plus, X, Users, AlertCircle, CheckCircle } from "lucide-react";
import { Badge } from "../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function OpsCashCollection({ user }) {
  const { toast } = useToast();
  const [locations, setLocations] = useState({ kitchen: null, outlets: [] });
  const [collections, setCollections] = useState([]);
  const [franchiseeReports, setFranchiseeReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  
  const [form, setForm] = useState({
    outlet_id: "",
    amount: "",
    franchisee_reported_amount: "",
    notes: "",
    source: "ops_manager" // ops_manager or franchisee
  });

  useEffect(() => {
    if (user?.user_id) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const locRes = await fetch(`${API_URL}/api/ops-extended/my-locations/${user.user_id}`, { credentials: "include" });
      if (locRes.ok) {
        const locData = await locRes.json();
        setLocations(locData);
        
        // Fetch today's collections by ops manager
        const today = new Date().toISOString().split("T")[0];
        const collRes = await fetch(`${API_URL}/api/ops-extended/cash-collections?ops_manager_id=${user.user_id}&date=${today}`, { credentials: "include" });
        if (collRes.ok) setCollections(await collRes.json());
        
        // Fetch today's franchisee cash reports
        const franchiseeRes = await fetch(`${API_URL}/api/ops-extended/franchisee-cash-reports?date=${today}`, { credentials: "include" });
        if (franchiseeRes.ok) {
          setFranchiseeReports(await franchiseeRes.json());
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.outlet_id || !form.amount) {
      toast({ title: "Error", description: "Please fill outlet and amount", variant: "destructive" });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ops-extended/cash-collections?ops_manager_id=${user.user_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          outlet_id: form.outlet_id,
          amount: parseFloat(form.amount),
          franchisee_reported_amount: form.franchisee_reported_amount ? parseFloat(form.franchisee_reported_amount) : null,
          notes: form.notes
        })
      });
      
      if (res.ok) {
        toast({ title: "Cash collection recorded successfully" });
        setShowForm(false);
        setForm({ outlet_id: "", amount: "", franchisee_reported_amount: "", notes: "", source: "ops_manager" });
        fetchData();
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to record collection", variant: "destructive" });
    }
  };

  const getOutletName = (outletId) => {
    const outlet = locations.outlets.find(o => o.outlet_id === outletId);
    return outlet ? outlet.name : "Unknown";
  };

  // Get franchisee report for an outlet
  const getFranchiseeReportForOutlet = (outletId) => {
    return franchiseeReports.find(r => r.outlet_id === outletId || r.user_id === outletId);
  };

  const getCollectedOutlets = () => {
    return collections.map(c => c.outlet_id);
  };

  const getPendingOutlets = () => {
    const collected = getCollectedOutlets();
    return locations.outlets.filter(o => !collected.includes(o.outlet_id));
  };

  const totalCollected = collections.reduce((sum, c) => sum + c.amount, 0);
  const totalFranchiseeReported = franchiseeReports.reduce((sum, r) => sum + (r.cash_amount || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  if (!locations.kitchen) {
    return (
      <Card data-testid="ops-cash-collection">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No Kitchen Assigned</h3>
            <p className="text-muted-foreground">Contact your administrator to assign you a kitchen.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="ops-cash-collection">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Ops Collected</p>
              <p className="text-3xl font-bold text-green-600">₹{totalCollected.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Franchisee Reported</p>
              <p className="text-3xl font-bold text-blue-600">₹{totalFranchiseeReported.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Outlets Collected</p>
              <p className="text-3xl font-bold">{collections.length}/{locations.outlets.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Pending</p>
              <p className="text-3xl font-bold text-yellow-600">{getPendingOutlets().length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Franchisee Reports Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500" />
            Franchisee Cash Reports (Today)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Cash amounts reported by franchisees. Use this as reference when recording your collection.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Franchisee</TableHead>
                <TableHead>Reported Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {franchiseeReports.map((report) => (
                <TableRow key={report.report_id || report.user_id}>
                  <TableCell className="font-medium">{report.user_name || report.franchisee_name || "Franchisee"}</TableCell>
                  <TableCell className="text-blue-600 font-semibold">₹{(report.cash_amount || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    {collections.find(c => c.outlet_id === report.outlet_id || c.franchisee_id === report.user_id) ? (
                      <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Collected</Badge>
                    ) : (
                      <Badge variant="outline" className="text-yellow-600"><AlertCircle className="h-3 w-3 mr-1" /> Pending Collection</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {report.created_at ? new Date(report.created_at).toLocaleTimeString() : "-"}
                  </TableCell>
                </TableRow>
              ))}
              {franchiseeReports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                    No franchisee reports submitted today
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Collection Form */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Record Cash Collection
          </CardTitle>
          {!showForm && (
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" /> Record Collection
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {showForm && (
            <div className="border rounded-lg p-4 mb-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">New Collection Entry</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Outlet *</Label>
                  <Select value={form.outlet_id} onValueChange={(v) => {
                    const franchiseeReport = getFranchiseeReportForOutlet(v);
                    setForm({ 
                      ...form, 
                      outlet_id: v,
                      franchisee_reported_amount: franchiseeReport?.cash_amount?.toString() || ""
                    });
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select outlet" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.outlets.map((o) => (
                        <SelectItem key={o.outlet_id} value={o.outlet_id}>
                          {o.name}
                          {getCollectedOutlets().includes(o.outlet_id) && " ✓"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Franchisee Reported Amount</Label>
                  <Input 
                    type="number" 
                    value={form.franchisee_reported_amount} 
                    onChange={(e) => setForm({ ...form, franchisee_reported_amount: e.target.value })} 
                    placeholder="As reported by franchisee"
                    className="bg-blue-50"
                    readOnly
                  />
                  <p className="text-xs text-muted-foreground mt-1">Auto-filled from franchisee report</p>
                </div>
              </div>

              <div>
                <Label>Your Collected Amount (₹) *</Label>
                <Input 
                  type="number" 
                  min={0}
                  value={form.amount} 
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} 
                  placeholder="Amount you actually collected"
                />
                {form.franchisee_reported_amount && form.amount && 
                  parseFloat(form.amount) !== parseFloat(form.franchisee_reported_amount) && (
                  <p className="text-xs text-yellow-600 mt-1">
                    ⚠️ Difference from franchisee report: ₹{Math.abs(parseFloat(form.amount) - parseFloat(form.franchisee_reported_amount)).toLocaleString()}
                  </p>
                )}
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={form.notes} 
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} 
                  placeholder="Any discrepancy notes, reasons, etc..." 
                />
              </div>

              <Button onClick={handleSubmit} className="w-full">Record Collection</Button>
            </div>
          )}

          {/* Today's Collections by Ops Manager */}
          <div>
            <h3 className="font-medium mb-3">Your Collections Today</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Outlet</TableHead>
                  <TableHead>Collected</TableHead>
                  <TableHead>Franchisee Reported</TableHead>
                  <TableHead>Difference</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {collections.map((collection) => {
                  const franchiseeAmt = collection.franchisee_reported_amount || 0;
                  const diff = collection.amount - franchiseeAmt;
                  return (
                    <TableRow key={collection.collection_id}>
                      <TableCell className="font-medium">{getOutletName(collection.outlet_id)}</TableCell>
                      <TableCell className="text-green-600 font-semibold">₹{collection.amount.toLocaleString()}</TableCell>
                      <TableCell className="text-blue-600">
                        {franchiseeAmt > 0 ? `₹${franchiseeAmt.toLocaleString()}` : "-"}
                      </TableCell>
                      <TableCell>
                        {franchiseeAmt > 0 ? (
                          <span className={diff === 0 ? "text-green-600" : diff > 0 ? "text-green-600" : "text-red-600"}>
                            {diff === 0 ? "Match" : diff > 0 ? `+₹${diff}` : `-₹${Math.abs(diff)}`}
                          </span>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">{collection.notes || "-"}</TableCell>
                      <TableCell className="text-sm">
                        {new Date(collection.created_at).toLocaleTimeString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {collections.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                      No collections recorded today
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pending Outlets */}
          {getPendingOutlets().length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium mb-3 text-yellow-600">Pending Outlets</h3>
              <div className="flex flex-wrap gap-2">
                {getPendingOutlets().map((outlet) => (
                  <div key={outlet.outlet_id} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                    {outlet.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
